/**
 * bson-free ObjectId helpers.
 *
 * The domain only needs to GENERATE and NORMALISE Mongo ObjectId *hex strings* (24 chars). The
 * authoritative ObjectId <-> Mongo conversion lives at the persistence boundary in web-api
 * (Infraestructure/Repositories/mongodb/*.ts), which keeps its own `bson`. DV-6306 already moved
 * the heavy lifting out of the domain (`Project.getProjection({})` serialises ids to strings via
 * JSON.parse(JSON.stringify)). Dropping `bson` here keeps the domain browser-safe (consumable
 * client-side) without touching that persistence path.
 */

const HEX = '0123456789abcdef'

/** Generates a valid 24-char hex ObjectId string: 4-byte timestamp + 16 random hex chars. */
export function generateObjectId(): string {
    const timestamp = Math.floor(Date.now() / 1000)
        .toString(16)
        .padStart(8, '0')
        .slice(-8)
    let rest = ''
    for (let i = 0; i < 16; i++) rest += HEX[Math.floor(Math.random() * 16)]
    return timestamp + rest
}

/**
 * Normalises any accepted _id shape — hex string, `{ $oid }`, or an object exposing
 * `toHexString()`/`toString()` — to a plain hex string. Mirrors the previous
 * `new ObjectId(getOID(value)).toHexString()` without depending on bson. Null/undefined yields a
 * freshly generated id; invalid input is returned as-is (lenient) rather than throwing.
 */
export function toObjectIdHex(value: unknown): string {
    if (value === null || value === undefined) return generateObjectId()
    let v: unknown = value
    if (typeof v === 'object') {
        const obj = v as { $oid?: unknown; toHexString?: () => string; toString?: () => string }
        if (obj.$oid !== undefined) v = obj.$oid
        else if (typeof obj.toHexString === 'function') v = obj.toHexString()
        else if (typeof obj.toString === 'function') v = obj.toString()
    }
    return String(v)
}
